import type {
  DeviationEvent,
  Game,
  GameRepertoireMatch,
  NodeRef,
  PositionNode,
  Repertoire,
  RepertoireEdge,
} from '../api/types';
import { playUci } from './chess';

/**
 * Mock-only game analysis for the §4 mock server: the §5.3 detector rules and the
 * v1.8 game-to-repertoire matching, evaluated over the mock's in-memory repertoires.
 * The real server computes both from canonical identity; this port exists so the
 * demo and E2E builds behave the same for repertoires a person builds in mock mode.
 */

export const START_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
const MATCH_PLY_LIMIT = 60;

export interface MockRepertoireGraph {
  repertoire: Repertoire;
  edges: RepertoireEdge[];
}

export interface MockAnalysisContext {
  /** The mock's canonical position for a FEN (by board, side, castling, ep). */
  positionByFen: (fen: string) => PositionNode | undefined;
}

function members(graph: MockRepertoireGraph): Set<string> {
  const ids = new Set([graph.repertoire.defaultRoot.position.positionId]);
  for (const edge of graph.edges) {
    if (!edge.enabled) continue;
    ids.add(edge.transition.from.positionId);
    ids.add(edge.transition.to.positionId);
  }
  return ids;
}

/** A stable stand-in reference for a position no repertoire contains. */
function offBookRef(fen: string): NodeRef {
  let hash = 0;
  for (const char of fen) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return { hashVersion: 1, zobristHash: `f${hash.toString(16).padStart(31, '0')}`, positionId: '0' };
}

function refOf(position: PositionNode | undefined, fen: string): NodeRef {
  return position
    ? { hashVersion: position.hashVersion, zobristHash: position.zobristHash, positionId: position.positionId }
    : offBookRef(fen);
}

function replay(game: Game): string[] {
  const fens = [game.initialFen ?? START_FEN];
  for (const uci of game.movesUci) {
    const next = playUci(fens[fens.length - 1], uci);
    if (!next) break;
    fens.push(next.fen);
  }
  return fens;
}

/** The §5.3 classification, move by move (no engine). */
export function mockDetect(game: Game, graph: MockRepertoireGraph, ctx: MockAnalysisContext): DeviationEvent[] {
  const inRepertoire = members(graph);
  const userColor = game.userColor ?? (graph.repertoire.side === 'black' ? 'b' : 'w');
  const fens = replay(game);
  const events: DeviationEvent[] = [];
  let source = ctx.positionByFen(fens[0]);
  let inside = source !== undefined && inRepertoire.has(source.positionId);
  for (let ply = 0; ply + 1 < fens.length; ply += 1) {
    const uci = game.movesUci[ply];
    const sideBefore = fens[ply].split(' ')[1] === 'b' ? 'b' : 'w';
    const outgoing = inside && source
      ? graph.edges.filter((edge) => edge.enabled && edge.transition.from.positionId === source!.positionId)
      : [];
    const matched = outgoing.find((edge) => edge.transition.uci === uci);
    const expected = outgoing
      .filter((edge) => edge.role !== 'avoid')
      .sort((a, b) => a.priority - b.priority || a.transition.uci.localeCompare(b.transition.uci))
      .map((edge) => edge.transition.uci);
    const target = ctx.positionByFen(fens[ply + 1]);
    const targetInside = target !== undefined && inRepertoire.has(target.positionId);
    let kind: DeviationEvent['kind'] | null = null;
    if (matched) {
      if (matched.role === 'avoid') kind = 'suboptimal';
      else if (matched.role === 'alternative') kind = 'branching';
    } else if (targetInside) {
      kind = inside ? 'transposition_entry' : 'transposition_reentry';
    } else {
      kind = sideBefore === userColor ? 'novelty' : 'opponent_novelty';
    }
    const base = {
      ply,
      source: refOf(source, fens[ply]),
      target: refOf(target, fens[ply + 1]),
      actualMove: uci,
      expectedMoves: expected,
      lossCp: null,
      explanation: null,
    };
    if (inside && sideBefore !== userColor && !outgoing.some((edge) => edge.role === 'opponent')) {
      events.push({ ...base, eventId: `${game.gameId}-${ply}-gap`, kind: 'coverage_gap' });
    }
    if (kind) events.push({ ...base, eventId: `${game.gameId}-${ply}`, kind });
    source = target;
    inside = targetInside;
  }
  return events;
}

function compatible(side: Repertoire['side'], userColor: Game['userColor']): boolean {
  if (!userColor || side === 'both') return true;
  return (side === 'white') === (userColor === 'w');
}

/** The v1.8 ranking: book plies, then matched plies, then name. */
export function mockMatch(game: Game, graphs: MockRepertoireGraph[], ctx: MockAnalysisContext): GameRepertoireMatch {
  const fens = replay({ ...game, movesUci: game.movesUci.slice(0, MATCH_PLY_LIMIT) });
  const positions = fens.map((fen) => ctx.positionByFen(fen)?.positionId);
  const matches = graphs
    .filter((graph) => compatible(graph.repertoire.side, game.userColor))
    .map((graph) => {
      const inRepertoire = members(graph);
      const member = positions.map((id) => id !== undefined && inRepertoire.has(id));
      let book = 0;
      if (member[0]) while (book + 1 < member.length && member[book + 1]) book += 1;
      return {
        repertoireId: graph.repertoire.repertoireId,
        name: graph.repertoire.name,
        side: graph.repertoire.side,
        bookPlies: book,
        matchedPlies: member.slice(1).filter(Boolean).length,
      };
    })
    .sort((a, b) => b.bookPlies - a.bookPlies || b.matchedPlies - a.matchedPlies || a.name.localeCompare(b.name));
  return { gameId: game.gameId, userColor: game.userColor ?? null, plyCount: game.movesUci.length, matches };
}
